import { z } from "zod/v3";
import { defaultMetadata, metadataSchema } from "./metadata";

export const AiBasics = z
  .object({
    name: z.string().min(1).optional(),
    headline: z.string().optional(),
    email: z.string().email().optional(),
    phone: z.string().optional(),
    location: z.string().optional(),
    websites: z.array(z.string()).default([]),
  })
  .partial();

export const AiExperienceItem = z.object({
  id: z
    .string()
    .describe("Canonical id string. Include for updates; omit to create a new item.")
    .optional(),
  company: z.string().optional(),
  position: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  location: z.string().optional(),
  bullets: z.array(z.string()).default([]),
});

export const AiEducationItem = z.object({
  id: z
    .string()
    .describe("Canonical id string. Include for updates; omit to create a new item.")
    .optional(),
  institution: z.string().optional(),
  degree: z.string().optional(),
  graduationDate: z.string().optional(),
  location: z.string().optional(),
  gpa: z.string().optional(),
  bullets: z.array(z.string()).default([]),
});

// Skills section (independent): each item is a category-like skill with optional keywords
export const AiSkillItem = z.object({
  id: z
    .string()
    .describe("Canonical id string. Include for updates; omit to create a new item.")
    .optional(),
  name: z.string().min(1).describe("Skill group/category name (e.g., 'iOS Development')"),
  keywords: z
    .array(z.string())
    .default([])
    .describe("Related tools/technologies (e.g., Objective-C, Swift, SwiftUI)"),
  description: z.string().optional().describe("Optional short description"),
  level: z.preprocess(Number, z.number().min(0).max(5)).optional().describe("0-5, optional"),
});

// Languages section: human languages
export const AiLanguageItem = z.object({
  id: z
    .string()
    .describe("Canonical id string. Include for updates; omit to create a new item.")
    .optional(),
  name: z.string().min(1).describe("Human language (e.g., English, Spanish, Urdu)"),
  description: z.string().optional().describe("Optional details (e.g., dialect, certifications)"),
  level: z.preprocess(Number, z.number().min(0).max(5)).optional().describe("0-5, optional"),
});

export const AiCertificationItem = z.object({
  id: z
    .string()
    .describe("Canonical id string. Include for updates; omit to create a new item.")
    .optional(),
  name: z.string().min(1),
  issuer: z.string().optional(),
  date: z.string().optional(),
  summary: z.string().optional(),
  link: z.string().optional().describe("URL to certification"),
});

export const AiAwardItem = z.object({
  id: z
    .string()
    .describe("Canonical id string. Include for updates; omit to create a new item.")
    .optional(),
  title: z.string().min(1),
  awarder: z.string().optional(),
  date: z.string().optional(),
  summary: z.string().optional(),
  link: z.string().optional().describe("URL to award reference"),
});

export const AiInterestItem = z.object({
  id: z
    .string()
    .describe("Canonical id string. Include for updates; omit to create a new item.")
    .optional(),
  name: z.string().min(1),
  keywords: z.array(z.string()).default([]),
});

export const AiProjectsItem = z.object({
  id: z
    .string()
    .describe("Canonical id string. Include for updates; omit to create a new item.")
    .optional(),
  name: z.string().optional(),
  role: z.string().optional(),
  tech: z.array(z.string()).default([]),
  bullets: z.array(z.string()).default([]),
  link: z.string().optional(),
});

export const AiReferenceItem = z.object({
  id: z
    .string()
    .describe("Canonical id string. Include for updates; omit to create a new item.")
    .optional(),
  name: z.string().optional(),
  company: z.string().optional(),
  position: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  notes: z.string().optional(),
  link: z.string().optional(),
});

export const AiProfileItem = z.object({
  id: z
    .string()
    .describe("Canonical id string. Include for updates; omit to create a new item.")
    .optional(),
  network: z.string().min(1).describe("Network or platform name (e.g., LinkedIn, GitHub)"),
  url: z.string().min(1).describe("Profile URL"),
  username: z.string().optional().describe("Optional username or handle"),
});

export const AiCustomItem = z.object({
  id: z
    .string()
    .describe("Canonical id string. Include for updates; omit to create a new item.")
    .optional(),
  name: z.string().optional(),
  description: z.string().optional(),
  date: z.string().optional(),
  location: z.string().optional(),
  summary: z.string().optional(),
  keywords: z.array(z.string()).default([]),
  url: z
    .object({
      label: z.string().optional(),
      href: z.string().optional(),
    })
    .optional(),
});

export const AiCustomSectionGroup = z.object({
  id: z.string().optional(),
  name: z.string().optional(),
  items: z.array(AiCustomItem).default([]),
});

export const AiMetadata = metadataSchema.deepPartial();

export const AiResume = z.object({
  basics: AiBasics.default({}),
  summary: z.string().default(""),
  experience: z.array(AiExperienceItem).default([]),
  education: z.array(AiEducationItem).default([]),
  skills: z.array(AiSkillItem).default([]),
  languages: z.array(AiLanguageItem).default([]),
  certifications: z.array(AiCertificationItem).default([]),
  awards: z.array(AiAwardItem).default([]),
  interests: z.array(AiInterestItem).default([]),
  projects: z.array(AiProjectsItem).default([]),
  profiles: z.array(AiProfileItem).default([]),
  references: z.array(AiReferenceItem).default([]),
  custom: z.record(z.string(), AiCustomSectionGroup).default({}),
  metadata: AiMetadata.default(defaultMetadata),
  targetRole: z.string().optional(),
  preferences: z
    .object({
      tone: z.enum(["concise", "balanced", "detailed"]).optional(),
      seniority: z.enum(["junior", "mid", "senior", "lead"]).optional(),
    })
    .default({})
    .optional(),
});

export type AiBasicsType = z.infer<typeof AiBasics>;
export type AiExperienceItemType = z.infer<typeof AiExperienceItem>;
export type AiEducationItemType = z.infer<typeof AiEducationItem>;
export type AiSkillsType = z.infer<typeof AiSkillItem>[];
export type AiProjectsItemType = z.infer<typeof AiProjectsItem>;
export type AiReferenceItemType = z.infer<typeof AiReferenceItem>;
export type AiProfileItemType = z.infer<typeof AiProfileItem>;
export type AiCustomItemType = z.infer<typeof AiCustomItem>;
export type AiCustomSectionGroupType = z.infer<typeof AiCustomSectionGroup>;
export type AiMetadataType = z.infer<typeof AiMetadata>;
export type AiResumeType = z.infer<typeof AiResume>;
